import mixin from '@/mixin/index.js';
import patientsServices from '@/services/patients.services.js';
import prescriptionsServices from '@/services/prescriptions.services';
import animals from "@/constants/animals.enum.js";
const {
    _isValidNumber,
    _getLabel,
    _isValidBirthday,
    _testCpf,
    _isValidEmail,
    _getMessage,
    _isValidName
} = mixin.methods;
const root = { root: true };
let patientSelectionGeneration = 0;
const PATIENTS_MODELS = [
    'name',
    'species',
    'sex',
    'weight',
    'hasBirthday',
    'birthday',
    'breed',
    'breedNotes',
    'age',
    'neutered',
    'bodyScore',
    'color',
    'bloodType',
    'hasMicrochip',
    'microchip',
    'hasGovRegistry',
    'govRegistryNumber',
    'hasOtherRegistry',
    'otherRegistryOrgan',
    'otherRegistryNumber',
    'hasInsurance',
    'insuranceProvider',
    'insurancePlan',
    'insuranceCardNumber',
    'insuranceCardValidity',
    'allergiesStatus',
    'allergies',
    'chronicConditionsStatus',
    'chronicConditions',
]
const OWNER_MODELS_EDIT = [
    'name',
    'cpf',
    'ownerHasCpf',
    'phone',
    'email',
    'address',
    'addressZipCode',
    'addressStreet',
    'addressNumber',
    'addressComplement',
    'addressNeighborhood',
    'addressCity',
    'addressState',
]

const TRI_STATE = ['yes', 'no', 'unsure'];
const HISTORY_STATUS = ['affirmed', 'denied'];
const BODY_SCORE_LABELS = [
    'BODY_SCORE_UNDEFINED',
    'BODY_SCORE_VERY_THIN',
    'BODY_SCORE_THIN',
    'BODY_SCORE_IDEAL',
    'BODY_SCORE_OVERWEIGHT',
    'BODY_SCORE_OBESE'
];
const getBodyScoreItems = () => BODY_SCORE_LABELS.map(label => _getLabel(label));
/* Patients Algorithms — built bottom-up so child references are concrete */

// 17 — Chronic conditions: status (affirmed/denied) → if affirmed, optional list
const patientChronicConditionsAlgorithm = () => ({
    type: 2,
    question: _getLabel('PATIENT_CHRONIC_STATUS_QUESTION'),
    model: 'chronicConditionsStatus',
    options: [
        {
            title: _getLabel('PATIENT_CHRONIC_AFFIRMED'),
            child: {
                type: 3,
                question: _getLabel('PATIENT_CHRONIC_DETAILS'),
                inputs: [{
                    rule: true,
                    model: 'chronicConditions',
                    kind: 'textfield',
                    placeholder: _getLabel('PATIENT_CHRONIC_DETAILS')
                }],
                child: null
            }
        },
        {
            title: _getLabel('PATIENT_CHRONIC_DENIED'),
            child: null
        }
    ]
});

// 16 — Allergies: status (affirmed/denied) → if affirmed, optional list
const patientAllergiesAlgorithm = () => ({
    type: 2,
    question: _getLabel('PATIENT_ALLERGIES_STATUS_QUESTION'),
    model: 'allergiesStatus',
    options: [
        {
            title: _getLabel('PATIENT_ALLERGIES_AFFIRMED'),
            child: {
                type: 3,
                question: _getLabel('PATIENT_ALLERGIES_DETAILS'),
                inputs: [{
                    rule: true,
                    model: 'allergies',
                    kind: 'textfield',
                    placeholder: _getLabel('PATIENT_ALLERGIES_DETAILS')
                }],
                child: patientChronicConditionsAlgorithm()
            }
        },
        {
            title: _getLabel('PATIENT_ALLERGIES_DENIED'),
            child: patientChronicConditionsAlgorithm()
        }
    ]
});

// 15 — Blood type (optional)
const patientBloodTypeAlgorithm = () => ({
    type: 3,
    question: _getLabel('BLOOD_TYPE'),
    inputs: [{
        rule: true,
        model: 'bloodType',
        kind: 'textfield',
        placeholder: _getLabel('BLOOD_TYPE')
    }],
    child: patientAllergiesAlgorithm()
});

// 14 — Neutered (Sim/Não/Incerto)
const patientNeuteredAlgorithm = () => ({
    type: 2,
    question: _getLabel('NEUTERED_QUESTION'),
    model: 'neutered',
    options: [
        { title: _getLabel('NO'), child: patientBloodTypeAlgorithm() },
        { title: _getLabel('YES'), child: patientBloodTypeAlgorithm() },
        { title: _getLabel('NEUTERED_UNCERTAIN'), child: patientBloodTypeAlgorithm() }
    ]
});

// 13 — Sex
const patientSexAlgorithm = () => ({
    type: 2,
    question: _getLabel('PATIENT_SEX'),
    model: 'sex',
    options: [_getLabel('MALE'), _getLabel('FEMALE')].map((sex) => ({
        title: sex,
        child: patientNeuteredAlgorithm()
    }))
});

// 12 — Color
const patientColorAlgorithm = () => ({
    type: 3,
    question: _getLabel('COLOR'),
    inputs: [{
        rule: true,
        model: 'color',
        kind: 'textfield',
        placeholder: _getLabel('COLOR')
    }],
    child: patientSexAlgorithm()
});

// 11 — Breed notes
const patientBreedNotesAlgorithm = () => ({
    type: 3,
    question: _getLabel('PATIENT_BREED_NOTES'),
    inputs: [{
        rule: true,
        model: 'breedNotes',
        kind: 'textfield',
        placeholder: _getLabel('PATIENT_BREED_NOTES')
    }],
    child: patientColorAlgorithm()
});

// 10 — Breed
const patientBreedAlgorithm = (species) => ({
    type: 3,
    question: _getLabel('PATIENT_BREED'),
    inputs: [{
        rule: (v) => !!v || _getLabel('BREED_ERROR'),
        model: 'breed',
        items: species === _getLabel('DOG') ? animals.DOG_BREEDS_BRAZILIAN : animals.CAT_BREEDS_BRAZILIAN,
        kind: 'select'
    }],
    child: patientBreedNotesAlgorithm()
});

// 9 — Body score (selector)
const patientBodyScoreAlgorithm = (species) => ({
    type: 3,
    question: _getLabel('PATIENT_BODY_SCORE_QUESTION'),
    inputs: [{
        rule: (v) => !!v || _getLabel('REQUIRED_FIELD'),
        model: 'bodyScore',
        items: getBodyScoreItems(),
        kind: 'select'
    }],
    child: patientBreedAlgorithm(species)
});

// 8 — Weight
const patientWeightAlgorithm = (species) => ({
    type: 3,
    question: _getLabel('PATIENT_WEIGHT'),
    inputs: [{
        rule: (v) => _isValidNumber(v) || _getLabel('WEIGHT_ERROR'),
        model: 'weight',
        kind: 'textfield'
    }],
    child: patientBodyScoreAlgorithm(species)
});

// 7 — Birthday / Age
const patientBirthdayAlgorithm = (species) => ({
    type: 3,
    question: _getLabel('BIRTHDAY'),
    inputs: [{
        rule: (v) => _isValidBirthday(v) || _getLabel('INVALID_DATE'),
        model: 'birthday',
        placeholder: 'DD/MM/YYYY',
        mask: '##/##/####',
        kind: 'textfield'
    }],
    child: patientWeightAlgorithm(species)
});
const patientAgeAlgorithm = (species) => ({
    type: 3,
    question: _getLabel('PATIENT_AGE'),
    inputs: [{
        rule: (v) => _isValidNumber(v) || _getLabel('AGE_ERROR'),
        model: 'age',
        kind: 'textfield'
    }],
    child: patientWeightAlgorithm(species)
});
const patientHasBirthdayAlgorithm = (species) => ({
    type: 2,
    question: _getLabel('PATIENT_HAS_BIRTHDAY'),
    model: 'hasBirthday',
    options: [
        { title: _getLabel('PATIENT_HAS_BIRTHDAY_NO'), child: patientAgeAlgorithm(species) },
        { title: _getLabel('PATIENT_HAS_BIRTHDAY_YES'), child: patientBirthdayAlgorithm(species) }
    ]
});

// 6 — Insurance: Sim/Não. If Sim → 4 optional sequential inputs.
const patientInsuranceAlgorithm = (species) => {
    const next = patientHasBirthdayAlgorithm(species);
    const validityNode = {
        type: 3,
        question: _getLabel('INSURANCE_CARD_VALIDITY'),
        inputs: [{
            rule: true,
            model: 'insuranceCardValidity',
            kind: 'textfield',
            mask: '##/##/####',
            placeholder: 'DD/MM/AAAA'
        }],
        child: next
    };
    const cardNumberNode = {
        type: 3,
        question: _getLabel('INSURANCE_CARD_NUMBER'),
        inputs: [{ rule: true, model: 'insuranceCardNumber', kind: 'textfield' }],
        child: validityNode
    };
    const planNode = {
        type: 3,
        question: _getLabel('INSURANCE_PLAN'),
        inputs: [{ rule: true, model: 'insurancePlan', kind: 'textfield' }],
        child: cardNumberNode
    };
    const providerNode = {
        type: 3,
        question: _getLabel('INSURANCE_PROVIDER_QUESTION'),
        inputs: [{ rule: true, model: 'insuranceProvider', kind: 'textfield' }],
        child: planNode
    };
    return {
        type: 2,
        question: _getLabel('PATIENT_INSURANCE_QUESTION'),
        model: 'hasInsurance',
        options: [
            { title: _getLabel('YES'), child: providerNode },
            { title: _getLabel('NO'), child: next }
        ]
    };
};

// 5 — Other registry tri-state. Sim → órgão (opt) → número (opt).
const patientOtherRegistryAlgorithm = (species) => {
    const next = patientInsuranceAlgorithm(species);
    const numberNode = {
        type: 3,
        question: _getLabel('PATIENT_OTHER_REGISTRY_NUMBER'),
        inputs: [{ rule: true, model: 'otherRegistryNumber', kind: 'textfield' }],
        child: next
    };
    const organNode = {
        type: 3,
        question: _getLabel('PATIENT_OTHER_REGISTRY_ORGAN'),
        inputs: [{ rule: true, model: 'otherRegistryOrgan', kind: 'textfield' }],
        child: numberNode
    };
    return {
        type: 2,
        question: _getLabel('PATIENT_OTHER_REGISTRY_QUESTION'),
        model: 'hasOtherRegistry',
        options: [
            { title: _getLabel('YES'), child: organNode },
            { title: _getLabel('NO'), child: next },
            { title: _getLabel('UNCERTAIN'), child: next }
        ]
    };
};

// 4 — Gov registry tri-state. Sim → número (opt).
const patientGovRegistryAlgorithm = (species) => {
    const next = patientOtherRegistryAlgorithm(species);
    const numberNode = {
        type: 3,
        question: _getLabel('PATIENT_GOV_REGISTRY_NUMBER'),
        inputs: [{ rule: true, model: 'govRegistryNumber', kind: 'textfield' }],
        child: next
    };
    return {
        type: 2,
        question: _getLabel('PATIENT_GOV_REGISTRY_QUESTION'),
        model: 'hasGovRegistry',
        options: [
            { title: _getLabel('YES'), child: numberNode },
            { title: _getLabel('NO'), child: next },
            { title: _getLabel('UNCERTAIN'), child: next }
        ]
    };
};

// 3 — Microchip tri-state. Sim → número (opt).
const patientMicrochipAlgorithm = (species) => {
    const next = patientGovRegistryAlgorithm(species);
    const numberNode = {
        type: 3,
        question: _getLabel('PATIENT_MICROCHIP_NUMBER'),
        inputs: [{ rule: true, model: 'microchip', kind: 'textfield' }],
        child: next
    };
    return {
        type: 2,
        question: _getLabel('PATIENT_MICROCHIP_QUESTION'),
        model: 'hasMicrochip',
        options: [
            { title: _getLabel('YES'), child: numberNode },
            { title: _getLabel('NO'), child: next },
            { title: _getLabel('UNCERTAIN'), child: next }
        ]
    };
};

// 2 — Species
const patientSpeciesAlgorithm = () => ({
    type: 2,
    question: _getLabel('PATIENT_SPECIES'),
    model: 'species',
    options: [_getLabel('DOG'), _getLabel('CAT')].map((species) => ({
        title: species,
        child: patientMicrochipAlgorithm(species)
    }))
});

// 1 — Name
const patientNameAlgorithm = () => ({
    type: 3,
    question: _getLabel('PATIENT_NAME'),
    inputs: [{
        rule: (v) => !!v || _getLabel('NAME_ERROR'),
        model: 'name',
        length: 40,
        kind: 'textfield'
    }],
    child: patientSpeciesAlgorithm()
});

/* Owners Algorithms */

// Address chain (CEP → city → state → neighborhood → street → number → complement)
const ownerAddressComplementAlgorithm = () => ({
    type: 3,
    question: _getLabel('ADDRESS_COMPLEMENT'),
    inputs: [{ rule: true, model: 'addressComplement', kind: 'textfield' }],
    child: null
});
const ownerAddressNumberAlgorithm = () => ({
    type: 3,
    question: _getLabel('ADDRESS_NUMBER'),
    inputs: [{ rule: true, model: 'addressNumber', kind: 'textfield' }],
    child: ownerAddressComplementAlgorithm()
});
const ownerAddressStreetAlgorithm = () => ({
    type: 3,
    question: _getLabel('ADDRESS_STREET'),
    inputs: [{ rule: true, model: 'addressStreet', kind: 'textfield' }],
    child: ownerAddressNumberAlgorithm()
});
const ownerAddressNeighborhoodAlgorithm = () => ({
    type: 3,
    question: _getLabel('ADDRESS_NEIGHBORHOOD'),
    inputs: [{ rule: true, model: 'addressNeighborhood', kind: 'textfield' }],
    child: ownerAddressStreetAlgorithm()
});
const ownerAddressStateAlgorithm = () => ({
    type: 3,
    question: _getLabel('ADDRESS_STATE'),
    inputs: [{ rule: true, model: 'addressState', kind: 'textfield' }],
    child: ownerAddressNeighborhoodAlgorithm()
});
const ownerAddressCityAlgorithm = () => ({
    type: 3,
    question: _getLabel('ADDRESS_CITY'),
    inputs: [{ rule: true, model: 'addressCity', kind: 'textfield' }],
    child: ownerAddressStateAlgorithm()
});
const ownerAddressZipAlgorithm = () => ({
    type: 3,
    question: _getLabel('ADDRESS_ZIP_CODE'),
    inputs: [{
        rule: true,
        model: 'addressZipCode',
        kind: 'textfield',
        mask: '#####-###',
        onChange: async (val) => {
            const sanitized = (val || '').replace(/[^0-9]/g, '');
            if (sanitized.length !== 8) return null;
            return await patientsServices.lookupCep(sanitized);
        }
    }],
    child: ownerAddressCityAlgorithm()
});

const ownerPhoneAlgorithm = (child = ownerAddressZipAlgorithm(), required = false, label) => ({
    type: 3,
    question: label || _getLabel('PHONE_LABEL'),
    inputs: [{
        rule: (v) => (!required || !!v) || _getLabel('INVALID_PHONE'),
        model: 'phone',
        mask: '(##) #####-####',
        kind: 'textfield'
    }],
    child: child ? child : null
});
const ownerEmailAlgorithm = (child = ownerPhoneAlgorithm(), required = false, label) => ({
    type: 3,
    question: label || _getLabel('TUTOR_EMAIL_OPTIONAL'),
    inputs: [{
        rule: (v) => (!v && !required) || _isValidEmail(v) || _getMessage('INVALID_EMAIL'),
        model: 'email',
        kind: 'textfield'
    }],
    child: child ? child : null
});

const ownerCpfAlgorithm = () => ({
    type: 3,
    question: _getLabel('OWNER_CPF'),
    inputs: [{
        rule: (v) => _testCpf(v) || _getLabel('SIGNUP_CPF_HINT'),
        model: 'cpf',
        mask: '###.###.###-##',
        kind: 'textfield'
    }],
    child: ownerEmailAlgorithm()
});

const ownerHasCpfAlgorithm = () => ({
    type: 2,
    question: _getLabel('OWNER_HAS_CPF_QUESTION'),
    model: 'ownerHasCpf',
    options: [
        { title: _getLabel('OWNER_HAS_CPF_YES'), child: ownerCpfAlgorithm() },
        { title: _getLabel('OWNER_HAS_CPF_LATER'), child: ownerEmailAlgorithm() }
    ]
});

const ownersAlgorithm = () => ({
    type: 3,
    question: _getLabel('TUTOR_NAME'),
    inputs: [{
        rule: (v) => _isValidName(v) || _getLabel('NAME_ERROR'),
        model: 'name',
        length: 40,
        kind: 'textfield'
    }],
    child: ownerHasCpfAlgorithm()
});

export default {
    confirmOwnerEmailAlgorithm: async ({ dispatch, state }) => {
        await dispatch('general/loadingHandler', {
            logEventArgs: 'confirmOwnerEmailAlgorithm',
            action: async () => {
                let owner = state.owner;
                if (!owner) {
                    owner = await patientsServices.getOwnerById(state.patient.ownerId);
                }
                dispatch("algorithms/openAlgorithmsNodes", {
                    nodes: ownerEmailAlgorithm(null, true, _getLabel('TUTOR_EMAIL_CONFIRMATION_BEFORE_SEND')),
                    args: { email: owner.email },
                    models: ['email']
                }, root);
            }
        }, root);
    },
    confirmOwnerPhoneAlgorithm: async ({ dispatch, state }) => {
        await dispatch('general/loadingHandler', {
            logEventArgs: 'confirmOwnerPhoneAlgorithm',
            action: async () => {
                let owner = state.owner;
                if (!owner) {
                    owner = await patientsServices.getOwnerById(state.patient.ownerId);
                }
                dispatch("algorithms/openAlgorithmsNodes", {
                    nodes: ownerPhoneAlgorithm(null, true, _getLabel('TUTOR_PHONE_CONFIRMATION_BEFORE_SEND')),
                    args: { phone: owner.phone },
                    models: ['phone']
                }, root);
            }
        }, root);
    },
    confirmOwnerPhoneAndEmailAlgorithm: async ({ dispatch, state }) => {
        await dispatch('general/loadingHandler', {
            logEventArgs: 'confirmOwnerPhoneAndEmailAlgorithm',
            action: async () => {
                let owner = state.owner;
                if (!owner) {
                    owner = await patientsServices.getOwnerById(state.patient.ownerId);
                }
                dispatch("algorithms/openAlgorithmsNodes", {
                    nodes: ownerEmailAlgorithm(ownerPhoneAlgorithm(null, true, _getLabel('TUTOR_PHONE_CONFIRMATION_BEFORE_SEND')), true, _getLabel('TUTOR_EMAIL_CONFIRMATION_BEFORE_SEND')),
                    args: { phone: owner.phone, email: owner.email },
                    models: ['email', 'phone']
                }, root);
            }
        }, root);
    },
    getPatientsAndOwners: async ({ dispatch, state }, search) => {
        dispatch("general/logEvent", 'getPatientsAndOwners', { root: true });
        if (search) {
            try {
                const [patients, owners] = await Promise.all([
                    patientsServices.getPatients(search),
                    patientsServices.getOwners(search)
                ]);
                state.patientsAndOwners = patients.concat(owners);
            } catch (error) {
                dispatch("general/showError", null, root);
            }
        }
    },
    selectPatient: ({ dispatch, state }, patient) => {
        ++patientSelectionGeneration;
        dispatch("general/logEvent", 'selectPatient', root);
        state.patient = patient;
        state.prescriptions = null;
        state.owner = null;
    },
    /**
     * Carrega o paciente pelo id da URL (rota direta / refresh de pagina).
     * Nunca lanca: devolve { ok, patient, status } para a view decidir o redirecionamento.
     */
    selectPatientById: async ({ dispatch, state }, patientId) => {
        const generation = ++patientSelectionGeneration;
        if (!patientId) return { ok: false, status: 404 };
        if (state.patient && state.patient.id === patientId) {
            return { ok: true, patient: state.patient };
        }
        let result = { ok: false, status: null };
        state.patient = null;
        state.owner = null;
        state.prescriptions = null;
        await dispatch('general/loadingHandler', {
            logEventArgs: 'selectPatientById',
            action: async () => {
                const response = await patientsServices.getPatientById(patientId);
                if (generation !== patientSelectionGeneration) { result = { ok: false, stale: true }; return; }
                const patient = Array.isArray(response) ? response[0] : response;
                if (!patient || !patient.id) {
                    result = { ok: false, status: 404 };
                    return;
                }
                state.patient = patient;
                state.prescriptions = null;
                state.owner = null;
                result = { ok: true, patient };
            },
            errorAction: (error) => {
                result = {
                    ok: false,
                    status: error && error.response ? error.response.status : null
                };
            }
        }, root);
        return result;
    },
    selectOwner: async ({ dispatch, state }, ownerId) => {
        await dispatch('general/loadingHandler', {
            logEventArgs: 'selectOwner',
            action: async () => {
                state.owner = await patientsServices.getOwnerById(ownerId);
            }
        }, root);
    },
    getPatientPrescriptions: async ({ dispatch, state }) => {
        await dispatch('general/loadingHandler', {
            logEventArgs: 'getPatientPrescriptions',
            action: async () => {
                const cursor = state.prescriptions && state.prescriptions.length
                    ? state.prescriptions[state.prescriptions.length - 1].lastUpdate : null;
                const prescriptions = await prescriptionsServices.getPrescriptions(state.patient.id, cursor);
                state.prescriptions = (state.prescriptions || []).concat(prescriptions);
                if (prescriptions.length === 0 && state.prescriptions.length > 0) {
                    dispatch("general/showAlert", { text: _getLabel('NO_MORE_PRESCRIPTIONS_FOUND') }, root);
                }
            }
        }, root);
    },
    openPatientAddition: ({ dispatch }) => {
        dispatch("general/logEvent", 'openPatientAddition', root);
        dispatch("algorithms/openAlgorithmsNodes", {
            nodes: patientNameAlgorithm()
        }, root);
    },
    openPatientEdition: ({ dispatch, state }) => {
        dispatch("general/logEvent", 'openPatientEdition', root);
        const p = state.patient;
        const triStateIndex = (val) => {
            const i = TRI_STATE.indexOf(val);
            return i === -1 ? undefined : i;
        };
        const historyStatusIndex = (val) => {
            const i = HISTORY_STATUS.indexOf(val);
            return i === -1 ? undefined : i;
        };
        const insurance = p.insurance || (p.insuranceProvider ? { provider: p.insuranceProvider } : undefined);
        const args = {
            ...p,
            species: p.species - 1,
            weight: p.weight != null ? p.weight.toString().replace('.', ',') : undefined,
            neutered: typeof p.neutered === 'number' ? p.neutered : undefined,
            bodyScore: typeof p.bodyScore === 'number' && p.bodyScore >= 0
                ? getBodyScoreItems()[p.bodyScore]
                : undefined,
            hasMicrochip: triStateIndex(p.hasMicrochip),
            hasGovRegistry: triStateIndex(p.hasGovRegistry),
            hasOtherRegistry: triStateIndex(p.hasOtherRegistry),
            hasInsurance: p.hasInsurance === 'yes' ? 0 : p.hasInsurance === 'no' ? 1 : undefined,
            insuranceProvider: insurance && insurance.provider,
            insurancePlan: insurance && insurance.plan,
            insuranceCardNumber: insurance && insurance.cardNumber,
            insuranceCardValidity: insurance && insurance.validity,
            allergiesStatus: historyStatusIndex(p.allergiesStatus),
            chronicConditionsStatus: historyStatusIndex(p.chronicConditionsStatus),
            allergies: Array.isArray(p.allergies)
                ? p.allergies.join(', ')
                : p.allergies,
            chronicConditions: Array.isArray(p.chronicConditions)
                ? p.chronicConditions.join(', ')
                : p.chronicConditions
        };
        dispatch("algorithms/openAlgorithmsNodes", {
            nodes: patientNameAlgorithm(),
            args,
            models: PATIENTS_MODELS
        }, root);
    },
    openOwnerAddition: ({ dispatch }) => {
        dispatch("general/logEvent", 'openOwnerAddition', root);
        dispatch("algorithms/openAlgorithmsNodes", {
            nodes: ownersAlgorithm()
        }, root);
    },
    openOwnerEdition: ({ dispatch, state }) => {
        dispatch("general/logEvent", 'openOwnerEdition', root);
        const o = state.owner || {};
        dispatch("algorithms/openAlgorithmsNodes", {
            nodes: ownersAlgorithm(),
            args: {
                ...o,
                ownerHasCpf: o.cpf ? 0 : undefined
            },
            models: OWNER_MODELS_EDIT
        }, root);
    },
    savePatient: async ({ dispatch, state, rootGetters }, isNewRecord) => {
        await dispatch('general/loadingHandler', {
            logEventArgs: 'savePatient',
            action: async () => {
                const models = rootGetters["algorithms/getModels"];
                const parseList = (value) => {
                    if (!value) return undefined;
                    if (Array.isArray(value)) return value;
                    return value.split(',').map(item => item.trim()).filter(item => !!item);
                };
                const triStateValue = (idx) => typeof idx === 'number' ? TRI_STATE[idx] : undefined;
                const historyStatusValue = (idx) => typeof idx === 'number' ? HISTORY_STATUS[idx] : undefined;
                const bodyScoreIndex = typeof models.bodyScore === 'string'
                    ? getBodyScoreItems().indexOf(models.bodyScore)
                    : models.bodyScore;
                const hasInsurance = models.hasInsurance === 0 ? 'yes' : models.hasInsurance === 1 ? 'no' : undefined;
                const insurance = hasInsurance === 'yes'
                    ? {
                        provider: models.insuranceProvider || undefined,
                        plan: models.insurancePlan || undefined,
                        cardNumber: models.insuranceCardNumber || undefined,
                        validity: models.insuranceCardValidity || undefined
                    }
                    : undefined;

                const payload = {
                    ...models,
                    species: models.species + 1,
                    ownerId: isNewRecord ? state.owner.id : state.patient.ownerId,
                    id: !isNewRecord ? state.patient.id : undefined,
                    bodyScore: typeof bodyScoreIndex === 'number' && bodyScoreIndex >= 0 ? bodyScoreIndex : undefined,
                    hasMicrochip: triStateValue(models.hasMicrochip),
                    hasGovRegistry: triStateValue(models.hasGovRegistry),
                    hasOtherRegistry: triStateValue(models.hasOtherRegistry),
                    hasInsurance,
                    allergiesStatus: historyStatusValue(models.allergiesStatus),
                    chronicConditionsStatus: historyStatusValue(models.chronicConditionsStatus),
                    allergies: parseList(models.allergies),
                    chronicConditions: parseList(models.chronicConditions),
                };
                if (insurance) payload.insurance = insurance;
                delete payload.insuranceProvider;
                delete payload.insurancePlan;
                delete payload.insuranceCardNumber;
                delete payload.insuranceCardValidity;
                if (models.hasMicrochip !== 0) delete payload.microchip;
                if (models.hasGovRegistry !== 0) delete payload.govRegistryNumber;
                if (models.hasOtherRegistry !== 0) {
                    delete payload.otherRegistryOrgan;
                    delete payload.otherRegistryNumber;
                }
                if (models.allergiesStatus !== 0) delete payload.allergies;
                if (models.chronicConditionsStatus !== 0) delete payload.chronicConditions;

                // O backend valida campos opcionais com ensureValidNonEmptyString, que
                // rejeita string vazia com HTTP 400. Campos apagados no formulario
                // chegam aqui como "", entao precisam sair do payload.
                Object.keys(payload).forEach(key => {
                    if (typeof payload[key] === 'string' && payload[key].trim() === '') {
                        delete payload[key];
                    } else if (payload[key] === undefined) {
                        delete payload[key];
                    }
                });

                state.patient = await patientsServices.postPatient(payload);
                state.patientsAndOwners = state.patientsAndOwners.filter(
                    patient => !(patient.id === state.patient.id && state.patient.species === patient.species)
                );
                state.patientsAndOwners.push(state.patient);
                if (isNewRecord) {
                    state.owner.patients.push(state.patient);
                }
            },
            errorAction(error) {
                dispatch("general/showError", null, root);
                throw error;
            }
        }, root);
    },
    saveOwner: async ({ dispatch, state, rootGetters }, isNewRecord) => {
        await dispatch('general/loadingHandler', {
            action: async () => {
                const models = { ...rootGetters["algorithms/getModels"] };
                if (models.ownerHasCpf === 1) {
                    delete models.cpf;
                }
                delete models.ownerHasCpf;
                const patients = isNewRecord ? [] : state.owner.patients;
                const payload = isNewRecord ? { ...models, patients } : { ...state.owner, ...models };
                state.owner = await patientsServices.postOwner(payload);
                state.owner.patients = patients;
                state.patientsAndOwners = state.patientsAndOwners.filter(
                    owner => !(owner.id === state.owner.id && typeof owner.species !== 'number' && typeof state.owner.species !== 'number')
                );
                state.patientsAndOwners.push(state.owner);
            },
            logEventArgs: 'saveOwner',
            errorAction(error) {
                if (error.response && error.response.data && error.response.data.message && error.response.data.message.includes('There is already an owner with this CPF')) {
                    dispatch("general/showError", _getLabel('CPF_ALREADY_REGISTERED'), root);
                } else {
                    dispatch("general/showError", null, root);
                }
                throw error;
            }
        }, root);
    },
    /* Owners Interface */
    getPatientShareById: async ({ dispatch, state }, id) => {
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'getPatientShareById',
            action: async () => {
                return await patientsServices.getPatientShareById(id);
            },
            errorAction(err) { console.log(err) }
        }, root);
    },

    getSharedClinicalHandoutFromId: async ({ dispatch }, {
        handoutId,
        shareId,
    }) => {
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'getSharedClinicalHandoutFromId',
            action: async () => {
                return await patientsServices.getSharedClinicalHandoutFromId(handoutId, shareId);
            }
        }, root);
    },

    clearPatientsAndOwners: ({ state }) => {
        ++patientSelectionGeneration;
        state.patientsAndOwners = [];
        state.patient = null;
        state.owner = null;
        state.prescriptions = null;
    }
}
